import {
  React,
  useState,
  useEffect,
} from 'react';
import {
  graphql,
  useStaticQuery,
} from 'gatsby';
import { XIcon } from '@heroicons/react/solid';
import { INLINES } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import Storage from '../../utils/Storage';
import {
  safeJsonParse,
  convertContentfulInlineLink,
} from '../../utils/dataConverter';
import {
  ALERT_TYPES,
  ALERT_IDS,
} from '../../constants';

const docComponentOptions = { renderNode: { [INLINES.HYPERLINK]: convertContentfulInlineLink } };

const AlertMessage = (props) => {
  const {
    id,
    messageType = '',
    message = {},
    onClick = () => {},
  } = props;

  const bgClassName = messageType === ALERT_TYPES.EMERGENCY
    ? 'bg-avenue-orange-500 text-white font-bold'
    : 'bg-avenue-lemon-600 text-white font-bold';

  const handleClick = (e) => {
    e.preventDefault();
    onClick(id);
  };

  const classNames = `w-full mb-px ${bgClassName}`.trim();

  return (
    <div className={classNames}>
      <div className="container mx-auto flex justify-between py-3 px-6">
        <div
          className="block w-full text-center"
        >
          {documentToReactComponents(safeJsonParse(message.raw), docComponentOptions)}
        </div>
        <button
          type="button"
          className="h-5 w-5 mt-1"
          onClick={handleClick}
        >
          <XIcon aria-hidden="true" />
          <span className="sr-only">
            Dismiss
          </span>
        </button>
      </div>
    </div>
  );
};

const AlertBanners = (props) => {
  const {
    contentfulAlertMessagesList = {},
  } = useStaticQuery(graphql`
      {
        contentfulAlertMessagesList (name: { eq: "Site wide alert messages"}) {
          name
          alertMessage {
            title
            start
            end
            messageType
            id
            message {
              raw
            }
          }
        }
      }
    `);

  const {
    alertMessage = [],
  } = contentfulAlertMessagesList;

  // only create storage instance once
  const [storage] = useState(new Storage());
  const [alerts, setAlerts] = useState();

  const filterMessages = (messages) => {
    const now = new Date();
    const nowTime = now.getTime();

    // contentful return null if no messages
    const alertMessages = Array.isArray(messages)
      ? messages
      : [];

    return alertMessages.filter((message = {}) => {
      if (!message.start || !message.end) {
        return false;
      }
      const startDateTime = new Date(message.start);
      const endDateTime = new Date(message.end);
      return (startDateTime.getTime() <= nowTime && endDateTime.getTime() >= nowTime);
    });
  };

  useEffect(() => {
    let contentfulAlerts = filterMessages(alertMessage);
    const dismissedAlertIds = storage.get(ALERT_IDS);

    if (Array.isArray(dismissedAlertIds)) {
      contentfulAlerts = contentfulAlerts.filter((alert) => !dismissedAlertIds.includes(alert.id));
    }

    setAlerts(contentfulAlerts);
  }, [alertMessage, storage]);

  const handleDismiss = (id = '') => {
    const filteredAlerts = alerts.filter((alert) => alert.id !== id);
    setAlerts(filteredAlerts);
    // store filtered alerts to storage so use won't see the dismissed alert again
    // when navigate to different page eg some news page
    const dismissedAlertIds = storage.get(ALERT_IDS) || [];

    if (!dismissedAlertIds.includes(id)) {
      dismissedAlertIds.push(id);
      storage.set(ALERT_IDS, dismissedAlertIds);
    }
  };

  if (!alerts) {
    return null;
  }

  return alerts.map((alertData) => (
    <AlertMessage
      {...alertData}
      onClick={handleDismiss}
      key={alertData.id}
    />
  ));
};

export default AlertBanners;
